/**
 * Fleet assignments — NEW V3.1 page.
 *
 * Table of all (agent, market) assignments grouped by market
 * (GET /api/v1/fleet/assignments), market-wide pause/resume-all, and an
 * "Apply config" flow: pick market → pick trading config version
 * (GET /api/v1/fleet/configs/trading) → multi-select enrolled agents →
 * paper/live mode → PUT /api/v1/fleet/assignments.
 */
import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon, Alert, Badge, Button, Code, Group, Modal, MultiSelect,
  Paper, SegmentedControl, Select, Stack, Table, Text, Title, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlayerPause, IconPlayerPlay, IconSettingsBolt } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fleetApi, tradingConfigItems, upgradeApi, type Assignment,
} from "../../api/fleet";
import { useMarket } from "../../hooks/useMarket";
import { useSSE } from "../../hooks/useSSE";

export function FleetAssignmentsPage() {
  const qc = useQueryClient();
  const [applyOpen, setApplyOpen] = useState(false);

  const assignmentsQ = useQuery({
    queryKey: ["fleet", "assignments"],
    queryFn: () => fleetApi.fleet.assignments(),
    refetchInterval: 15_000,
    retry: false,
  });
  const agentsQ = useQuery({
    queryKey: ["fleet", "agents"],
    queryFn: () => fleetApi.agents.list(),
  });

  useSSE({});

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["fleet", "assignments"] });
  const pauseAll = useMutation({
    mutationFn: (marketId: string) => fleetApi.fleet.pauseAssignments({ market_id: marketId }),
    onSuccess: () => { invalidate(); notifications.show({ color: "yellow", message: "Market paused on all bots." }); },
    onError: () => notifications.show({ color: "red", message: "Pause failed." }),
  });
  const resumeAll = useMutation({
    mutationFn: (marketId: string) => fleetApi.fleet.resumeAssignments({ market_id: marketId }),
    onSuccess: () => { invalidate(); notifications.show({ color: "green", message: "Market resumed on all bots." }); },
    onError: () => notifications.show({ color: "red", message: "Resume failed." }),
  });
  const pauseOne = useMutation({
    mutationFn: (a: Assignment) =>
      fleetApi.fleet.pauseAssignments({ market_id: a.market_id, agent_ids: [a.agent_id] }),
    onSuccess: invalidate,
  });
  const resumeOne = useMutation({
    mutationFn: (a: Assignment) =>
      fleetApi.fleet.resumeAssignments({ market_id: a.market_id, agent_ids: [a.agent_id] }),
    onSuccess: invalidate,
  });

  const agentName = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of agentsQ.data ?? []) m.set(a.id, a.display_name);
    return (id: string, fallback?: string) => fallback ?? m.get(id) ?? id;
  }, [agentsQ.data]);

  const grouped = useMemo(() => {
    const by = new Map<string, Assignment[]>();
    for (const a of assignmentsQ.data ?? []) {
      const list = by.get(a.market_id) ?? [];
      list.push(a);
      by.set(a.market_id, list);
    }
    return [...by.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [assignmentsQ.data]);

  return (
    <>
      <Group justify="space-between" mb="lg">
        <Title order={2}>Assignments</Title>
        <Button leftSection={<IconSettingsBolt size={16} />} onClick={() => setApplyOpen(true)}>
          Apply config
        </Button>
      </Group>

      {assignmentsQ.isError && (
        <Alert color="yellow" mb="md">
          Assignments unavailable — the CC server does not expose
          <Code>/api/v1/fleet/assignments</Code> yet.
        </Alert>
      )}
      {assignmentsQ.isLoading && <Text c="dimmed">Loading…</Text>}
      {!assignmentsQ.isLoading && !assignmentsQ.isError && grouped.length === 0 && (
        <Text c="dimmed">No assignments yet — use "Apply config" to create them.</Text>
      )}

      <Stack gap="lg">
        {grouped.map(([marketId, rows]) => {
          const allPaused = rows.every((r) => r.paused);
          const nonePaused = rows.every((r) => !r.paused);
          return (
            <Paper key={marketId} p="md" withBorder>
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <Badge size="lg" variant="outline">{marketId}</Badge>
                  <Text size="sm" c="dimmed">{rows.length} bot(s)</Text>
                </Group>
                <Group gap="xs">
                  <Button
                    size="xs" variant="light" color="yellow"
                    leftSection={<IconPlayerPause size={14} />}
                    disabled={allPaused || pauseAll.isPending}
                    onClick={() => pauseAll.mutate(marketId)}
                  >
                    Pause all
                  </Button>
                  <Button
                    size="xs" variant="light" color="green"
                    leftSection={<IconPlayerPlay size={14} />}
                    disabled={nonePaused || resumeAll.isPending}
                    onClick={() => resumeAll.mutate(marketId)}
                  >
                    Resume all
                  </Button>
                </Group>
              </Group>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Bot</Table.Th>
                    <Table.Th>Config version</Table.Th>
                    <Table.Th>Mode</Table.Th>
                    <Table.Th>State</Table.Th>
                    <Table.Th>Activated</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((a) => (
                    <Table.Tr key={`${a.agent_id}:${a.market_id}`}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {agentName(a.agent_id, a.agent_display_name)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace" truncate maw={220}>
                          {a.active_config_version_id ?? "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={a.mode === "live" ? "red" : "blue"} variant="light">
                          {a.mode}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={a.paused ? "yellow" : "green"} variant="light">
                          {a.paused ? "paused" : "active"}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {a.activated_at ? new Date(a.activated_at).toLocaleString() : "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6} justify="flex-end" wrap="nowrap">
                          <Tooltip label="Pause on this bot">
                            <ActionIcon
                              size="sm" variant="light" color="yellow"
                              disabled={a.paused}
                              onClick={() => pauseOne.mutate(a)}
                            >
                              <IconPlayerPause size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Resume on this bot">
                            <ActionIcon
                              size="sm" variant="light" color="green"
                              disabled={!a.paused}
                              onClick={() => resumeOne.mutate(a)}
                            >
                              <IconPlayerPlay size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          );
        })}
      </Stack>

      <ApplyConfigModal opened={applyOpen} onClose={() => setApplyOpen(false)} />
    </>
  );
}

/** Market → config version → agents (multi) → mode → PUT assignments. */
/** Apply one trading config to one or more bots for a market. Used by the Bot
 *  Farm master controls (preselected bots) and each bot's dashboard. */
export function ApplyConfigModal({ opened, onClose, initialAgentIds }: {
  opened: boolean;
  onClose: () => void;
  initialAgentIds?: string[];
}) {
  const qc = useQueryClient();
  const { markets, marketId: currentMarketId } = useMarket();
  const [marketId, setMarketId] = useState<string | null>(currentMarketId);
  const [configVersionId, setConfigVersionId] = useState<string | null>(null);
  const [agentIds, setAgentIds] = useState<string[]>(initialAgentIds ?? []);
  const [mode, setMode] = useState<"paper" | "live">("paper");
  // Re-seed the target list each time the modal opens with a new selection.
  useEffect(() => {
    if (opened) { setAgentIds(initialAgentIds ?? []); setPreview(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const agentsQ = useQuery({
    queryKey: ["fleet", "agents"],
    queryFn: () => fleetApi.agents.list(),
    enabled: opened,
  });
  const configsQ = useQuery({
    queryKey: ["fleet", "configs", "trading", marketId ?? ""],
    queryFn: () => fleetApi.fleet.tradingConfigs({ market_id: marketId ?? undefined, limit: 100 }),
    enabled: opened && Boolean(marketId),
    retry: false,
  });
  const configs = tradingConfigItems(configsQ.data);

  // CFG-02: a multi-bot activation shows the exact target list (current config,
  // overrides retained, paused state, online state) and only proceeds with the
  // preview hash of that list. "Accepted" is reported — application is
  // confirmed per device via control.applied / acks, never by the HTTP 200.
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof upgradeApi.controls.previewAssignments>> | null>(null);
  const previewMut = useMutation({
    mutationFn: () => upgradeApi.controls.previewAssignments({ market_id: marketId!, config_version_id: configVersionId!, agent_ids: agentIds, mode }),
    onSuccess: setPreview,
    onError: (err) => notifications.show({ color: "red", message: err instanceof Error ? err.message : "Preview failed." }),
  });
  const apply = useMutation({
    mutationFn: () =>
      fleetApi.fleet.applyAssignments({
        market_id: marketId!,
        config_version_id: configVersionId!,
        agent_ids: agentIds,
        mode,
        preview_hash: preview?.preview_hash,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["fleet", "assignments"] });
      const res = r as { applied?: string[]; skipped_overrides?: string[]; applies_from?: string };
      notifications.show({
        color: "yellow",
        title: "Activation accepted (not yet confirmed by devices)",
        message: `${res.applied?.length ?? agentIds.length} bot(s) on ${marketId} from ${res.applies_from ? new Date(res.applies_from).toLocaleTimeString() : "next window"}; ${res.skipped_overrides?.length ?? 0} override(s) retained. Applied revisions arrive via device acknowledgements.`,
      });
      setPreview(null);
      onClose();
    },
    onError: (err) =>
      notifications.show({
        color: "red",
        message: err instanceof Error ? err.message : "Failed to apply config.",
      }),
  });

  const enrolledAgents = (agentsQ.data ?? []).filter((a) => a.status !== "disabled");

  return (
    <Modal opened={opened} onClose={onClose} title="Apply trading config" size="lg">
      <Stack>
        <Select
          label="Market"
          placeholder="Pick market…"
          data={markets.map((m) => ({ value: m.market_id, label: m.display_name }))}
          value={marketId}
          onChange={(v) => { setMarketId(v); setConfigVersionId(null); }}
          searchable
        />
        <Select
          label="Trading config version"
          placeholder={configsQ.isError ? "Config catalogue unavailable" : "Pick config version…"}
          description="From /api/v1/fleet/configs/trading — the engine groups agents by (market, config version)."
          data={configs.map((c) => ({
            value: c.config_version_id,
            label: `${c.config_version_id.slice(0, 12)}…${c.is_active ? " (active)" : ""}${c.notes ? ` — ${c.notes}` : ""}`,
          }))}
          value={configVersionId}
          onChange={setConfigVersionId}
          disabled={!marketId || configsQ.isError}
          searchable
        />
        {configsQ.isError && (
          <Alert color="yellow">
            <Code>/api/v1/fleet/configs/trading</Code> is not implemented on the CC server yet.
          </Alert>
        )}
        <MultiSelect
          label="Agents"
          placeholder="Select enrolled bots…"
          data={enrolledAgents.map((a) => ({
            value: a.id,
            label: `${a.display_name} (${a.status})`,
          }))}
          value={agentIds}
          onChange={setAgentIds}
          searchable
          clearable
        />
        <div>
          <Text size="sm" fw={500} mb={4}>Mode</Text>
          <SegmentedControl
            data={[
              { value: "paper", label: "Paper" },
              { value: "live", label: "Live" },
            ]}
            value={mode}
            onChange={(v) => setMode(v as "paper" | "live")}
          />
        </div>
        {mode === "live" && (
          <Alert color="red" variant="light">
            Live mode executes real orders with the agents' wallets.
          </Alert>
        )}
        {preview && (
          <Alert color="blue" variant="light" title={`Exact targets (${preview.targets.length}) — applies from ${new Date(preview.applies_from).toLocaleTimeString()}`}>
            <Stack gap={2}>
              {preview.targets.map((t) => (
                <Text key={String(t.agent_id)} size="xs">
                  {String(t.display_name)}: {String(t.current_config_version_id ?? "none")} → {configVersionId}
                  {t.paused ? " · PAUSED (stays paused)" : ""}{t.online ? "" : " · offline (queued durably)"}
                </Text>
              ))}
              {preview.overrides_retained.length > 0 && (
                <Text size="xs" c="dimmed">Overrides retained: {preview.overrides_retained.map((t) => String(t.display_name)).join(", ")}</Text>
              )}
              <Text size="xs" c="dimmed">Activation never resumes a paused bot and never changes existing positions' management version.</Text>
            </Stack>
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          {agentIds.length > 1 && !preview ? (
            <Button
              disabled={!marketId || !configVersionId}
              loading={previewMut.isPending}
              onClick={() => previewMut.mutate()}
            >
              Preview {agentIds.length} target(s)
            </Button>
          ) : (
            <Button
              disabled={!marketId || !configVersionId || agentIds.length === 0}
              loading={apply.isPending}
              onClick={() => apply.mutate()}
            >
              {preview ? `Confirm activation for ${preview.targets.length} bot(s)` : `Apply to ${agentIds.length || "…"} bot(s)`}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
